import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'

/**
 * Live-refresh the open record. Subscribes to Supabase realtime for the
 * record row, its comments, and its submissions, and invalidates the relevant
 * TanStack Query caches on any change — so status transitions, new comments,
 * and submission saves made elsewhere show up without a manual refresh.
 *
 * Row visibility is enforced by RLS on the realtime channel. If the realtime
 * publication is unavailable the subscription simply delivers nothing and the
 * UI falls back to query staleness — no errors.
 */
export function useRecordRealtime(projectId: string | undefined, recordId: string | undefined) {
  const qc = useQueryClient()

  useEffect(() => {
    if (!projectId || !recordId) return

    const invalidate = () => {
      qc.invalidateQueries({ queryKey: ['record', recordId] })
      qc.invalidateQueries({ queryKey: ['record-forms', projectId, recordId] })
      qc.invalidateQueries({ queryKey: ['field-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['record-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['decline-comments', recordId] })
      qc.invalidateQueries({ queryKey: ['summary-card', projectId, recordId] })
      qc.invalidateQueries({ queryKey: ['final-submit-check', recordId] })
      qc.invalidateQueries({ queryKey: ['records', projectId] })
    }

    const channel = supabase
      .channel(`record-${recordId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'records', filter: `id=eq.${recordId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'comments', filter: `record_id=eq.${recordId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'submissions', filter: `record_id=eq.${recordId}` }, invalidate)
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [projectId, recordId, qc])
}
